import React, { useCallback, useEffect, useState } from 'react';
import useMount from 'react-use/lib/useMount';
import { RouteComponentProps, Redirect } from 'react-router';
import _ from 'lodash';
import qs from 'qs';
import { useHistory } from 'react-router-dom';
import { Loading } from '@tradingblock/components';
import { GetUrlParams } from '@tradingblock/api';
import { IStorageEngine } from '@tradingblock/types';
import { useLocalStorage, TokenFromStorage, SaveTokenInStorage } from '@tradingblock/storage';
import Config from '../config';
import { useDispatcher } from '../data/global/hooks';
import { useStateSelector, refreshTokenSelector } from '../data/global/dataSelectors';
import { Actions } from '../data/global/actions';
import { verifyTokenWithOutcome, verifyToken, tokenCheck, extractApiTokenFromJwt } from '../utilities/auth';
import { LoginRedirectUrl } from '../utilities/url';
import { usePageSettings } from '../framework/WrappedRoute';
import { LoginJwtValue } from '@tradingblock/types';

type TokenPayload = LoginJwtValue & { apiToken: string; token: string };

type TryGetTokenResult =
  | { kind: 'success'; payload: TokenPayload }
  | { kind: 'no_token' }
  | { kind: 'invalid_token' }
  | { kind: 'network_error'; message: string };

type BootstrapPhase =
  | 'loading'
  | 'authenticated_redirect'
  | 'session_restored'
  | 'needs_login_redirect'
  | 'network_error';

const tryGetOrigin = async (storage: IStorageEngine, originFromUrl: string, originFromState: string) => {
  const originValue = originFromUrl;
  const originFromLS = localStorage.getItem('origin');
  let parseOriginLS = '';

  if (typeof originFromLS === 'string') {
    parseOriginLS = JSON.parse(originFromLS);
  }

  if (!originValue && !originFromLS) {
    console.warn('origin found was invalid');
    return undefined;
  }

  if (!originValue) {
    return parseOriginLS;
  }
  localStorage.setItem('origin', JSON.stringify(originValue));
  return originValue;
};

/** Pre–TBFE-2301 token load (non-dev environments). */
const tryGetTokenLegacy = async (
  storage: IStorageEngine,
  tokenFromUrl: string | undefined,
  tokensFromState: { token: string | undefined }
): Promise<TokenPayload | undefined> => {
  const tokenFromLS = await TokenFromStorage(storage);
  const tokenVal = tokenFromUrl || tokenFromLS;
  const { token } = tokensFromState;

  if (!tokenVal && !token) {
    console.warn('[AuthenticationPage] legacy: token not found from url or local storage');
    return undefined;
  }

  if (tokenFromUrl && !Config.isVirtual) {
    const extractedApiToken = extractApiTokenFromJwt(tokenFromUrl);
    const apiToken = extractedApiToken || tokenFromUrl;

    const tokenCheckResult = await tokenCheck(apiToken);
    if (!tokenCheckResult) {
      console.warn('[AuthenticationPage] legacy: TokenCheck failed for URL token');
      return undefined;
    }
  }

  const tokenResult = tokenVal ? await verifyToken(tokenVal) : token ? await verifyToken(token) : undefined;
  if (!tokenResult) {
    console.warn('[AuthenticationPage] legacy: stored or supplied JWT was invalid');
    return undefined;
  }
  const { value, decoded } = tokenResult;
  const tokenPayload: TokenPayload = { ...decoded, apiToken: decoded.token, token: value };
  await SaveTokenInStorage(storage, value);
  return tokenPayload;
};

/** TBFE-2301 dev-only: explicit network vs invalid outcomes and cached public key. */
const tryGetTokenResilient = async (
  storage: IStorageEngine,
  tokenFromUrl: string | undefined,
  tokensFromState: { token: string | undefined }
): Promise<TryGetTokenResult> => {
  const tokenFromLS = await TokenFromStorage(storage);
  const tokenVal = tokenFromUrl || tokenFromLS;
  const { token } = tokensFromState;

  if (!tokenVal && !token) {
    console.warn('[AuthenticationPage] token not found from url or local storage');
    return { kind: 'no_token' };
  }

  if (tokenFromUrl && !Config.isVirtual) {
    const extractedApiToken = extractApiTokenFromJwt(tokenFromUrl);
    const apiToken = extractedApiToken || tokenFromUrl;

    const tokenCheckResult = await tokenCheck(apiToken);
    if (!tokenCheckResult) {
      console.warn('[AuthenticationPage] TokenCheck failed for URL token');
      return { kind: 'invalid_token' };
    }
  }

  const jwtToVerify = tokenVal || token;
  if (!jwtToVerify) {
    return { kind: 'no_token' };
  }

  const outcome = await verifyTokenWithOutcome(jwtToVerify);
  if (outcome.kind === 'network') {
    console.warn('[AuthenticationPage] verifyToken network outcome', outcome.message);
    return { kind: 'network_error', message: outcome.message };
  }
  if (outcome.kind === 'invalid') {
    console.warn('[AuthenticationPage] stored or supplied JWT was invalid');
    return { kind: 'invalid_token' };
  }

  const { value, decoded } = outcome.result;
  const tokenPayload: TokenPayload = { ...decoded, apiToken: decoded.token, token: value };
  await SaveTokenInStorage(storage, value);
  return { kind: 'success', payload: tokenPayload };
};

/** Pre–TBFE-2301 bootstrap (beta / production / local): immediate login redirect on any failure. */
const AuthenticationPageLegacy: React.FC<RouteComponentProps<{}>> = ({ location }) => {
  const { dispatch } = useDispatcher();
  const urlParams = qs.parse(_.drop(location.hash, 1).join(''));

  const tokenFromUrl = _.isString(urlParams.id_token) ? (urlParams.id_token as string) : undefined;
  const storage = useLocalStorage();
  const tokensFromState = useStateSelector(refreshTokenSelector);
  const originFromState = useStateSelector(s => s.auth.origin);
  const isExpired = useStateSelector(s => s.auth.isExpired);
  const params = GetUrlParams(location.search);
  const nextPageFromUrlSearchParams = decodeURIComponent(params['next'] || '/');
  const grp = process.env.REACT_APP_TB_SITE_GROUP;
  const urlSearchParamsFromNextPage = GetUrlParams(nextPageFromUrlSearchParams);
  const nextPagePathName: string = nextPageFromUrlSearchParams.split('?')[0] || '/';
  const history = useHistory();

  let nextPageSearchParams: string | undefined;

  if (Object.keys(urlSearchParamsFromNextPage).length > 0) {
    const urlSearchParams = new URLSearchParams();

    if (urlSearchParamsFromNextPage['showAccountUpdateModal']) {
      urlSearchParams.append('showAccountUpdateModal', urlSearchParamsFromNextPage['showAccountUpdateModal']);
    }

    if (urlSearchParamsFromNextPage['accountHolderIndex']) {
      urlSearchParams.append('accountHolderIndex', urlSearchParamsFromNextPage['accountHolderIndex']);
    }

    nextPageSearchParams = urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : '';
  }

  const [currentToken, setCurrentToken] = useState<string | null | undefined>();

  useMount(() =>
    tryGetTokenLegacy(storage, tokenFromUrl, tokensFromState)
      .then(tokenRes => {
        if (tokenRes) {
          if (tokenRes.pending) {
            history.push(`/no-account?grp=${grp}`);
            return;
          }
          dispatch(Actions.receiveToken(tokenRes));
          if (window.location.pathname.includes('/auth')) {
            setCurrentToken(tokenRes.token);
          }
        } else {
          setCurrentToken(null);
        }
      })
      .catch(err => {
        console.warn('[AuthenticationPage] legacy token verification errored', err);
        setCurrentToken(null);
      })
  );

  useMount(() =>
    tryGetOrigin(storage, urlParams.grp, originFromState)
      .then(originRes => {
        if (originRes) {
          dispatch(Actions.setOrigin({ origin: originRes }));
        }
      })
      .catch(err => {
        console.warn('[AuthenticationPage] legacy origin errored', err);
      })
  );

  useEffect(() => {
    if (isExpired === true) {
      setCurrentToken(null);
    }
  }, [isExpired]);

  useEffect(() => {
    if (currentToken === null) {
      window.location.href = `${Config.loginUrl}?redirect=${LoginRedirectUrl}&next=/&grp=${grp}`;
    }
  }, [currentToken, grp]);

  return (
    <div>
      {!_.isNil(currentToken) && (
        <Redirect
          to={{
            pathname: nextPagePathName,
            search: nextPageSearchParams,
          }}
        />
      )}
    </div>
  );
};

/** TBFE-2301 resilient bootstrap — active only when `Config.tbfe2301ResilientDashboardAuth`. */
const AuthenticationPageResilient: React.FC<RouteComponentProps<{}>> = ({ location }) => {
  const { dispatch } = useDispatcher();
  const urlParams = qs.parse(_.drop(location.hash, 1).join(''));

  const tokenFromUrl = _.isString(urlParams.id_token) ? (urlParams.id_token as string) : undefined;
  const storage = useLocalStorage();
  const tokensFromState = useStateSelector(refreshTokenSelector);
  const originFromState = useStateSelector(s => s.auth.origin);
  const isExpired = useStateSelector(s => s.auth.isExpired);
  const params = GetUrlParams(location.search);
  const nextPageFromUrlSearchParams = decodeURIComponent(params['next'] || '/');
  const grp = process.env.REACT_APP_TB_SITE_GROUP;
  const urlSearchParamsFromNextPage = GetUrlParams(nextPageFromUrlSearchParams);
  const nextPagePathName: string = nextPageFromUrlSearchParams.split('?')[0] || '/';
  const history = useHistory();

  let nextPageSearchParams: string | undefined;

  if (Object.keys(urlSearchParamsFromNextPage).length > 0) {
    const urlSearchParams = new URLSearchParams();

    if (urlSearchParamsFromNextPage['showAccountUpdateModal']) {
      urlSearchParams.append('showAccountUpdateModal', urlSearchParamsFromNextPage['showAccountUpdateModal']);
    }

    if (urlSearchParamsFromNextPage['accountHolderIndex']) {
      urlSearchParams.append('accountHolderIndex', urlSearchParamsFromNextPage['accountHolderIndex']);
    }

    nextPageSearchParams = urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : '';
  }

  const [bootstrapPhase, setBootstrapPhase] = useState<BootstrapPhase>('loading');
  const [networkMessage, setNetworkMessage] = useState<string>('');

  const redirectToLogin = useCallback(() => {
    window.location.href = `${Config.loginUrl}?redirect=${LoginRedirectUrl}&next=/&grp=${grp}`;
  }, [grp]);

  const runBootstrap = useCallback(async () => {
    setBootstrapPhase('loading');
    setNetworkMessage('');
    try {
      const result = await tryGetTokenResilient(storage, tokenFromUrl, tokensFromState);
      switch (result.kind) {
        case 'success':
          if (result.payload.pending) {
            history.push(`/no-account?grp=${grp}`);
            return;
          }
          dispatch(Actions.receiveToken(result.payload));
          if (window.location.pathname.includes('/auth')) {
            setBootstrapPhase('authenticated_redirect');
          } else {
            setBootstrapPhase('session_restored');
          }
          return;
        case 'no_token':
        case 'invalid_token':
          setBootstrapPhase('needs_login_redirect');
          return;
        case 'network_error':
          setNetworkMessage(result.message);
          setBootstrapPhase('network_error');
          return;
      }
    } catch (err) {
      console.error('[AuthenticationPage] bootstrap threw', err);
      setNetworkMessage(err instanceof Error ? err.message : 'bootstrap_error');
      setBootstrapPhase('network_error');
    }
  }, [dispatch, grp, history, storage, tokenFromUrl, tokensFromState]);

  useMount(() => {
    void runBootstrap();
  });

  useMount(() =>
    tryGetOrigin(storage, urlParams.grp, originFromState)
      .then(originRes => {
        if (originRes) {
          dispatch(Actions.setOrigin({ origin: originRes }));
        }
      })
      .catch(err => {
        console.warn('[AuthenticationPage] origin errored', err);
      })
  );

  useEffect(() => {
    if (isExpired !== true) {
      return;
    }
    if (bootstrapPhase === 'loading' || bootstrapPhase === 'network_error') {
      return;
    }
    console.warn('[AuthenticationPage] session marked expired; redirecting to login');
    setBootstrapPhase('needs_login_redirect');
  }, [isExpired, bootstrapPhase]);

  useEffect(() => {
    if (bootstrapPhase === 'needs_login_redirect') {
      redirectToLogin();
    }
  }, [bootstrapPhase, redirectToLogin]);

  if (bootstrapPhase === 'loading') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
        }}
      >
        <Loading />
      </div>
    );
  }

  if (bootstrapPhase === 'network_error') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 16,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <p>
          Could not reach the authentication service to verify your session. Your login was not cleared—check that the UI
          API is running, then retry.
        </p>
        {networkMessage ? (
          <p style={{ fontSize: 12, color: '#666' }}>
            <code>{networkMessage}</code>
          </p>
        ) : null}
        <button type="button" onClick={() => void runBootstrap()}>
          Retry
        </button>
      </div>
    );
  }

  if (bootstrapPhase === 'session_restored') {
    return null;
  }

  if (bootstrapPhase === 'authenticated_redirect') {
    return (
      <Redirect
        to={{
          pathname: nextPagePathName,
          search: nextPageSearchParams,
        }}
      />
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
      }}
    >
      <Loading />
    </div>
  );
};

export const AuthenticationPage: React.FC<RouteComponentProps<{}>> = props => {
  usePageSettings({ name: 'Authentication', access: 'public' });
  if (Config.tbfe2301ResilientDashboardAuth) {
    return <AuthenticationPageResilient {...props} />;
  }
  return <AuthenticationPageLegacy {...props} />;
};
